import { gcm } from '@noble/ciphers/aes.js';
import { bytesToHex, bytesToUtf8, hexToBytes, utf8ToBytes } from '@noble/ciphers/utils.js';
import { scryptAsync } from '@noble/hashes/scrypt.js';
import * as Crypto from 'expo-crypto';
import { gunzipSync, gzipSync } from 'fflate';

import { base64ToBytes, bytesToBase64 } from '@/lib/base64';

/**
 * Cifra de backup do Kontto — AES-256-GCM com chave derivada da senha via scrypt.
 *
 * 100% local, sem backend: o arquivo cifrado vai pra nuvem DO USUARIO (Drive/Files).
 * O scrypt e' lento de proposito (resistencia a forca bruta), mas roda 1x por senha
 * (a chave derivada e' cacheada no SecureStore — ver lib/backupPassphrase.ts), entao
 * os backups seguintes usam so o AES, que e' barato.
 */

export interface KdfParams {
    N: number;
    r: number;
    p: number;
    dkLen: number;
}

/**
 * Parametros do scrypt calibrados para React Native/Hermes.
 *
 * Com o backup indo pra NUVEM do usuario (Drive — ver ADR 0027/0028), o modelo de
 * ameaca muda: o arquivo cifrado passa a estar exposto a comprometimento remoto e a
 * senha vira a UNICA defesa contra forca bruta OFFLINE (sem rate-limit). Por isso
 * subimos N de 1024 pra 2^14: memory-hard ~16MB/hash (resiste a paralelismo de GPU),
 * ~4s ao ativar a senha em aparelho real — sem os ~32MB/8s do 2^15, que arriscariam
 * OOM/jank no Android de entrada que domina nosso publico.
 *
 * Custo pago 1x na criacao da senha: a chave derivada e' cacheada no SecureStore
 * (backupPassphrase.ts) e salt/params viajam no envelope, entao o dia a dia e o restore
 * em outro aparelho seguem sem re-derivar. Retrocompativel: quem ja tinha senha mantem os
 * params antigos (guardados) ate recriar a senha — backups velhos decifram pelos params
 * do proprio envelope.
 */
export const DEFAULT_KDF: KdfParams = { N: 16384, r: 8, p: 1, dkLen: 32 };

const ENVELOPE_FORMAT = 'kontto-backup-enc';
const SALT_BYTES = 16;
const NONCE_BYTES = 12; // padrao do AES-GCM
const SCRYPT_ASYNC_TICK_MS = 4;

export type EnvelopeVersion = 1 | 2;
/** Como o `ct` está escrito no JSON. Ausente = `hex` (v1). */
export type EnvelopeEncoding = 'hex' | 'base64';
/** O que foi feito com o texto ANTES de cifrar. Ausente = nada (v1). */
export type EnvelopeCompression = 'none' | 'gzip';

/**
 * Envelope do backup (`.kontto`), dos segmentos do sync (`.kseg`) e do verificador de chave.
 *
 * v1: JSON do payload → AES-256-GCM → hex. O hex DOBRA o tamanho e o JSON vai sem comprimir:
 *     o backup real de ~1,4 k lançamentos dava 3,9 MB no arquivo.
 * v2 (ADR 0177): JSON compacto → gzip → AES-256-GCM → base64. Mesmo backup: ~210 KB.
 *     Comprime ANTES de cifrar (depois não comprime) e decifra ANTES de descomprimir (a tag
 *     GCM protege o gunzip de entrada adulterada). `enc`/`comp` são explícitos pra um leitor
 *     futuro não adivinhar.
 *
 * Leitor aceita os dois sempre. Escritor sai em `ENVELOPE_WRITE_VERSION` (mão única, como o
 * schema: app antigo NÃO lê v2 — ele tenta hex, falha na tag e diz "senha incorreta").
 */
export interface BackupEnvelope {
    format: typeof ENVELOPE_FORMAT;
    version: EnvelopeVersion;
    kdf: { algo: 'scrypt'; salt: string } & KdfParams;
    cipher: { algo: 'aes-256-gcm'; nonce: string };
    enc?: EnvelopeEncoding;
    comp?: EnvelopeCompression;
    /** ciphertext + tag GCM, em `enc` (hex quando ausente). */
    ct: string;
}

/**
 * Versão que o app ESCREVE quando nada o segura (backup, auto-backup, snapshot do Drive):
 * a 2 desde a 1.0.71 (ADR 0181). Com o sync ligado, o backup e os segmentos saem na versão
 * NEGOCIADA com os outros aparelhos do espaço (lib/sync/capabilities.ts): um app antigo no
 * espaço segura todo mundo em v1 até atualizar. O verificador de chave do sync fica em v1
 * sempre (é minúsculo e um app antigo precisa abri-lo pra diagnosticar senha divergente).
 */
export const ENVELOPE_WRITE_VERSION: EnvelopeVersion = 2;

/** Nível do gzip: 6 já dá ~12× num backup; acima disso custa CPU no aparelho por quase nada. */
const GZIP_LEVEL = 6;

/** Bytes aleatorios criptograficamente seguros (expo-crypto, nativo). */
export function randomBytes(length: number): Uint8Array {
    return Crypto.getRandomBytes(length);
}

export function generateSalt(): Uint8Array {
    return randomBytes(SALT_BYTES);
}

/** Deriva a chave simetrica de 32 bytes a partir da senha + sal (scrypt). */
export async function deriveKey(
    passphrase: string,
    salt: Uint8Array,
    params: KdfParams = DEFAULT_KDF,
): Promise<Uint8Array> {
    const pw = utf8ToBytes(passphrase.normalize('NFKC'));
    return scryptAsync(pw, salt, {
        N: params.N,
        r: params.r,
        p: params.p,
        dkLen: params.dkLen,
        asyncTick: SCRYPT_ASYNC_TICK_MS,
    });
}

/** Cifra um texto e devolve o envelope (JSON) pronto pra salvar. */
export function encryptString(
    plaintext: string,
    key: Uint8Array,
    salt: Uint8Array,
    params: KdfParams = DEFAULT_KDF,
    version: EnvelopeVersion = ENVELOPE_WRITE_VERSION,
): string {
    const nonce = randomBytes(NONCE_BYTES);
    const plain = utf8ToBytes(plaintext);
    const kdf = { algo: 'scrypt' as const, salt: bytesToHex(salt), N: params.N, r: params.r, p: params.p, dkLen: params.dkLen };
    const cipher = { algo: 'aes-256-gcm' as const, nonce: bytesToHex(nonce) };
    if (version === 2) {
        const ct = gcm(key, nonce).encrypt(gzipSync(plain, { level: GZIP_LEVEL }));
        const envelope: BackupEnvelope = { format: ENVELOPE_FORMAT, version: 2, kdf, cipher, enc: 'base64', comp: 'gzip', ct: bytesToBase64(ct) };
        return JSON.stringify(envelope);
    }
    const ct = gcm(key, nonce).encrypt(plain);
    const envelope: BackupEnvelope = { format: ENVELOPE_FORMAT, version: 1, kdf, cipher, ct: bytesToHex(ct) };
    return JSON.stringify(envelope);
}

/** Decifra com uma chave ja derivada. Lanca se a chave estiver errada (tag GCM invalida). */
export function decryptWithKey(envelope: BackupEnvelope, key: Uint8Array): string {
    const nonce = hexToBytes(envelope.cipher.nonce);
    const ct = envelope.enc === 'base64' ? base64ToBytes(envelope.ct) : hexToBytes(envelope.ct);
    const body = gcm(key, nonce).decrypt(ct);
    const plaintext = envelope.comp === 'gzip' ? gunzipSync(body) : body;
    return bytesToUtf8(plaintext);
}

/** Decifra a partir da senha (deriva a chave com o sal embutido no envelope). */
export async function decryptWithPassphrase(envelope: BackupEnvelope, passphrase: string): Promise<string> {
    const salt = hexToBytes(envelope.kdf.salt);
    const key = await deriveKey(passphrase, salt, envelope.kdf);
    return decryptWithKey(envelope, key);
}

/** Tenta interpretar um conteudo como envelope cifrado do Kontto. */
export function parseEnvelope(content: string): BackupEnvelope | null {
    if (!content.includes(ENVELOPE_FORMAT)) return null;
    try {
        const parsed = JSON.parse(content);
        if (
            parsed?.format === ENVELOPE_FORMAT &&
            (parsed?.version === 1 || parsed?.version === 2 || parsed?.version === undefined) &&
            typeof parsed?.kdf?.salt === 'string' &&
            typeof parsed?.cipher?.nonce === 'string' &&
            typeof parsed?.ct === 'string' &&
            (parsed?.enc === undefined || parsed.enc === 'hex' || parsed.enc === 'base64') &&
            (parsed?.comp === undefined || parsed.comp === 'none' || parsed.comp === 'gzip')
        ) {
            return parsed as BackupEnvelope;
        }
    } catch {
        // nao e' JSON valido
    }
    return null;
}

export function isEncryptedBackup(content: string): boolean {
    return parseEnvelope(content) != null;
}
